import {
  failRumOperation,
  startRumOperation,
  succeedRumOperation
} from "@/lib/datadog.js";

export type FlowName = "reschedule_job" | "reassign_job";
export type FlowOutcome = "completed" | "errored" | "abandoned";

interface ActiveFlow {
  key: string;
  submitted: boolean;
  context: Record<string, unknown>;
}

// One flow per job and name at a time. Keyed by the job so the store action that
// finishes a flow can find the one the UI started, without the UI handing a
// handle through modals. The RUM operation key is a counter, so no job id leaves
// the browser.
const active = new Map<string, ActiveFlow>();
let sequence = 0;

const registryKey = (name: FlowName, jobId: string | number) =>
  `${name}:${jobId}`;

/**
 * Mark the user's intent to reschedule or reassign a job. Starting a flow that
 * is still open means the user moved on without finishing it, so the earlier
 * one is closed as abandoned first.
 */
export function beginFlow(
  name: FlowName,
  jobId: string | number,
  context: Record<string, unknown> = {}
): void {
  settleFlow(name, jobId, "abandoned");
  sequence += 1;
  const key = String(sequence);
  active.set(registryKey(name, jobId), { key, context, submitted: false });
  startRumOperation(name, key, context);
}

/** Close a flow with its outcome. A flow that is not open is ignored. */
export function settleFlow(
  name: FlowName,
  jobId: string | number,
  outcome: FlowOutcome
): void {
  const id = registryKey(name, jobId);
  const flow = active.get(id);
  if (!flow) return;

  active.delete(id);
  if (outcome === "completed") {
    succeedRumOperation(name, flow.key, flow.context);
  } else {
    failRumOperation(
      name,
      flow.key,
      outcome === "errored" ? "error" : "abandoned",
      flow.context
    );
  }
}

/**
 * The user has committed to the change. The popover that held the form may now
 * close before the save finishes, and closing it must not read as walking away.
 */
export function submitFlow(name: FlowName, jobId: string | number): void {
  const flow = active.get(registryKey(name, jobId));
  if (flow) flow.submitted = true;
}

/** A popover closed. Abandons the flow unless the user already submitted it. */
export function abandonFlow(name: FlowName, jobId: string | number): void {
  if (active.get(registryKey(name, jobId))?.submitted) return;
  settleFlow(name, jobId, "abandoned");
}

export function isFlowOpen(name: FlowName, jobId: string | number): boolean {
  return active.has(registryKey(name, jobId));
}

interface FlowPayload {
  appointmentId: string | number;
  scheduled_time?: string;
  arrival_window_start?: string;
  technician_profile_id?: number | null;
  technician_profile_ids?: number[];
}

/** The flows a save touches, read off the fields it writes. */
export function flowsForPayload(payload: FlowPayload): FlowName[] {
  const flows: FlowName[] = [];
  if (
    payload.scheduled_time !== undefined ||
    payload.arrival_window_start !== undefined
  ) {
    flows.push("reschedule_job");
  }
  if (
    payload.technician_profile_id !== undefined ||
    payload.technician_profile_ids !== undefined
  ) {
    flows.push("reassign_job");
  }
  return flows;
}

/**
 * Run a save and settle the flows it carries out. A flow the UI began earlier
 * (a drag) keeps its entry point; a save with no earlier intent, such as the
 * drawer's dialogs, begins its own at the moment of the save.
 */
export async function trackFlows<T>(
  payload: FlowPayload,
  run: () => Promise<T>
): Promise<T> {
  const flows = flowsForPayload(payload);
  flows.forEach((name) => {
    if (!isFlowOpen(name, payload.appointmentId)) {
      beginFlow(name, payload.appointmentId, { entry_point: "dialog" });
    }
  });
  try {
    const result = await run();
    flows.forEach((name) =>
      settleFlow(name, payload.appointmentId, "completed")
    );
    return result;
  } catch (error) {
    flows.forEach((name) => settleFlow(name, payload.appointmentId, "errored"));
    throw error;
  }
}

/** Begin the flows a board drop starts, before its dialogs and save run. */
export function beginDragFlows(
  jobId: string | number,
  changed: { time: boolean; technician: boolean },
  board: "v1" | "v2"
): void {
  const context = { entry_point: "drag_drop", board };
  if (changed.time) beginFlow("reschedule_job", jobId, context);
  if (changed.technician) beginFlow("reassign_job", jobId, context);
}
